import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import UspBarStyles from '@stories/Widgets/UspBar/UspBar.styles';
import styled, { createGlobalStyle } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    // Set header height variables here
    --headerHeight: 244px;


    @media ${until(Device.DesktopSmall)} {
      --headerHeight: 98px;
    }
  }
`;
const Container = styled.header `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  
  z-index: 100;

  &[data-is-sticky='true'] {
    padding-top: ${fluid(12, 16)};

    @media ${until(Device.DesktopSmall)} {
      padding-left: 16px;
      padding-right: 16px;
    }

    ${UspBarStyles.Container} {
      display: none;
    }
  }

`;
const Inner = styled.div `
  ${siteWide};

  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto auto 65px;
  gap: 24px ${fluid(4, 16)};
  align-items: center;
  padding-top: ${fluid(12, 24)};

  [data-is-sticky='true'] & {
    grid-template-rows: auto;
    gap: 0;
    padding-top: 0;

    @media ${until(Device.DesktopSmall)} {
      background-color: ${brand.accent.darkBlue.darkBlue100};
      padding: 7px 8px 7px 14px;
      border-radius: 1000000000px;
      justify-content: space-between;
    }
  }

  @media ${until(Device.DesktopSmall)} {
    display: flex;
  }
`;
const LogoWrapper = styled.div `
  flex: 0 0 66px;
  display: flex;

  @media ${from(Device.DesktopSmall)} {
    grid-row: span 3;
    grid-column: 1 / span 2;
  }

  [data-is-sticky='true'] & {
    @media ${from(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const LogoLink = styled.a `
  svg {
    width: 100%;
    max-width: 250px;

    @media ${until(Device.DesktopSmall)} {
      height: 66px;
    }

    [data-is-sticky='true'] & {
      @media ${until(Device.DesktopSmall)} {
        height: 42px;
      }
    }

    [data-m-active='true'] & {
      @media ${until(Device.DesktopSmall)} {
        height: 84px;
      }
    }
  }
`;
const Logo = styled.img ``;
const TopArea = styled.div `
  grid-row: 1;
  grid-column: 5 / span 19;

  @media ${from(Device.Tablet)} {
    grid-column: 8 / span 17;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 3 / span 10;
  }

  &[data-indent='true'] {
    grid-column: 6 / span 19;

    @media ${from(Device.DesktopSmall)} {
      grid-column: 4 / span 9;
    }
  }

  [data-is-sticky='true'] & {
    @media ${from(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const TopGrid = styled.div `
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${fluid(8, 24)};

  @media ${until(Device.DesktopSmall)} {
    gap: 8px;
  }

  &[data-menu-open='true'] {
    @media ${until(Device.DesktopSmall)} {
      justify-content: space-between;
    }
  }
`;
const SecondaryLogo = styled.div `
  flex: 1 1 auto;
  display: flex;
  gap: 16px;
  align-items: flex-end;

  @media ${until(Device.DesktopSmall)} {
    flex: 0 1 80px;
  }

  [data-is-sticky='true'] [data-menu-open='false'] & {
    display: none;
  }
`;
const SecondaryLogoImg = styled.img `
  aspect-ratio: 130 / 36;
  max-width: 130px;

  @media ${until(Device.DesktopSmall)} {
    max-width: 100%;
  }
`;
const LogoSubText = styled.p `
  ${fonts.gotham.medium};
  color: ${brand.accent.darkBlue.darkBlue100};
  font-size: 12px;
  line-height: 16px;
  margin: 0;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const SearchBoxContainer = styled.div `
  flex: 0 1 548px;

  [data-is-sticky='true'] & {
    @media ${from(Device.DesktopSmall)} {
      flex: 1 1 460px;

      min-width: ${fluid(300, 460)};
    }
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const SearchBox = styled.form `
  width: 100%;
  padding: 6px 80px 6px 32px;
  background-color: ${brand.global.white};
  border-radius: 100000000px;
  position: relative;
  height: ${fluid(50, 60)};
  display: flex;
`;
const SearchInput = styled.input `
  ${fonts.gotham.regular};
  font-size: 16px;
  line-height: 23px;
  color: ${brand.global.black};
  width: 100%;
  border: none;
`;
const SearchButton = styled.button `
  ${ButtonReset};
  ${transition('background-color')};
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: ${fluid(42, 52)};
  height: ${fluid(42, 52)};
  background-color: ${brand.primary.pear.pear100};
  border-radius: 50%;
  border: ${fluid(2, 4)} solid ${brand.primary.pear.pear100};
  display: grid;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background-color: ${brand.primary.pear.pear50};
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${brand.global.black};
  }
`;
const CtaArea = styled.div `
  grid-column: 10 / span 3;
  display: flex;
  justify-content: flex-end;

  @media ${until(Device.DesktopSmall)} {
    gap: 8px;
    flex: 1 0 auto;

    ${ButtonStyles.Container} {
      --fgColour: ${brand.global.white};
      --buttonHeight: 42px;
      width: 42px;
      height: 42px;
    }
  }
`;
const MobileSearchButton = styled.button `
  ${ButtonReset};
  ${transition('background-color')};

  background: ${brand.primary.pear.pear100};
  border-radius: 100000000px;
  border: 2px solid ${brand.primary.pear.pear100};
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    color: ${brand.global.black};
  }

  &:hover {
    background: ${brand.primary.pear.pear50};
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MainNav = styled.div `
  grid-row: 2;
  grid-column: 3 / span 10;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }

  [data-is-sticky='true'] & {
    @media ${from(Device.DesktopSmall)} {
      grid-column: 1 / span 12;
    }
  }
`;
const Nav = styled.nav `
  display: flex;
  justify-content: space-between;
  padding: 0 24px;
  border-radius: 100000000px;
  position: relative;

  background-color: ${brand.accent.darkBlue.darkBlue100};

  [data-is-sticky='true'] & {
    padding: 0 5px 0 24px;
  }
`;
const ScrolledSearchArea = styled.li `
  display: none;

  [data-is-sticky='true'] & {
    @media ${from(Device.DesktopSmall)} {
      display: inline-flex;
      align-items: center;
    }
  }
`;
const NavItem = styled.div `
  
`;
const NavLink = styled.a `
  ${fonts.gotham.medium};
  color: ${brand.global.white};
  padding: 24px 16px;
  font-size: 16px;
  line-height: 23px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  ${NavItem}:hover &,
  ${NavItem}:focus-within & {
    text-decoration: underline;

    svg {
      transform: rotate(180deg);
    }
  }

  svg {
    ${transition('transform')};
    width: 16px;
    height: 16px;
  }

  ${NavItem}:focus-within & {
    outline: 2px solid ${brand.global.white};
    outline-offset: -4px;
  }
`;
const NavDropdown = styled.div `
  position: absolute;
  top: 100%;
  display: none;


  [aria-expanded='true'] &, 
  ${NavItem}:hover &,
  ${NavItem}:focus-within & {
    display: block;
  }

  &[data-align='left'] {
    right: auto;
  }

  &[data-align='right'] {
    left: auto;
    right: 0;
  }

  ${NavItem}:first-child & {
    left: 0;
  }

  /* Background.Pear */
  &[data-background='pear'] {
    --light-bg-color: ${brand.primary.pear.pearBg};
    --bg-color: ${brand.primary.pear.pear25};
    --featured-cicle-color: ${brand.primary.pear.pear100};
  }

  /* Background.Blue */
  &[data-background='blue'] {
    --light-bg-color: ${brand.primary.blue.blueBg};
    --bg-color: ${brand.primary.blue.blue25};
    --featured-cicle-color: ${brand.primary.blue.blue100};
  }

  /* Background.DarkBlue */
  &[data-background='dark-blue'] {
    --light-bg-color: ${brand.accent.darkBlue.darkBlueBg};
    --bg-color: ${brand.accent.darkBlue.darkBlue25};
    --featured-cicle-color: ${brand.accent.darkBlue.darkBlue100};
  }

  /* Background.ForrestGreen */
  &[data-background='forrest-green'] {
    --light-bg-color: ${brand.accent.forrestGreen.forrestGreenBg};
    --bg-color: ${brand.accent.forrestGreen.forrestGreen25};
    --featured-cicle-color: ${brand.accent.forrestGreen.forrestGreen100};
  }

  /* Background.Purple */
  &[data-background='purple'] {
    --light-bg-color: ${brand.secondary.purple.purpleBg};
    --bg-color: ${brand.secondary.purple.purple25};
    --featured-cicle-color: ${brand.secondary.purple.purple100};
  }

  /* Background.Yellow */
  &[data-background='yellow'] {
    --light-bg-color: ${brand.secondary.yellow.yellowBg};
    --bg-color: ${brand.secondary.yellow.yellow25};
    --featured-cicle-color: ${brand.secondary.yellow.yellow100};
  }

  /* Background.Orange */
  &[data-background='orange'] {
    --light-bg-color: ${brand.secondary.orange.orangeBg};
    --bg-color: ${brand.secondary.orange.orange25};
    --featured-cicle-color: ${brand.secondary.orange.orange100};
  }

  /* Background.EngageGreen */
  &[data-background='engage-green'] {
    --bg-color: ${brand.engagePrimary.green.green25};
    --featured-cicle-color: ${brand.engagePrimary.green.green100};
  }

  /* Background.EngageSeaGreen */
  &[data-background='engage-sea-green'] {
    --bg-color: ${brand.engagePrimary.seaGreen.seaGreen25};
    --featured-cicle-color: ${brand.engagePrimary.seaGreen.seaGreen100};
  }

  /* Background.EngageTeal */
  &[data-background='engage-teal'] {
    --bg-color: ${brand.engagePrimary.teal.teal25};
    --featured-cicle-color: ${brand.engagePrimary.teal.teal100};
  }

  /* Background.EngageLime */
  &[data-background='engage-lime'] {
    --bg-color: ${brand.engagePrimary.lime.lime25};
    --featured-cicle-color: ${brand.engagePrimary.lime.lime100};
  }

  /* Background.EngageSky */
  &[data-background='engage-sky'] {
    --bg-color: ${brand.engagePrimary.sky.sky25};
    --featured-cicle-color: ${brand.engagePrimary.sky.sky100};
  }

  /* Background.EngagePurple */
  &[data-background='engage-purple'] {
    --bg-color: ${brand.engageSecondary.purple.purple25};
    --featured-cicle-color: ${brand.engageSecondary.purple.purple100};
  }

  /* Background.EngageLilac */
  &[data-background='engage-lilac'] {
    --bg-color: ${brand.engageSecondary.lilac.lilac25};
    --featured-cicle-color: ${brand.engageSecondary.lilac.lilac100};
  }

  /* Background.EngageTangerine */
  &[data-background='engage-tangerine'] {
    --bg-color: ${brand.engageSecondary.tangerine.tangerine25};
    --featured-cicle-color: ${brand.engageSecondary.tangerine.tangerine100};
  }

  /* Background.EngageLavender */
  &[data-background='engage-lavender'] {
    --bg-color: ${brand.engageSecondary.lavender.lavender25};
    --featured-cicle-color: ${brand.engageSecondary.lavender.lavender100};
  }

  /* Background.EngagePink */
  &[data-background='engage-pink'] {
    --bg-color: ${brand.engageSecondary.pink.pink25};
    --featured-cicle-color: ${brand.engageSecondary.pink.pink100};
  }

  /* Background.EngageYellow */
  &[data-background='engage-yellow'] {
    --bg-color: ${brand.engageSecondary.yellow.yellow25};
    --featured-cicle-color: ${brand.engageSecondary.yellow.yellow100};
  }

  /* Background.White */
  &[data-background='white'] {
    --bg-color: ${brand.global.white};
  }
`;
const NavDropDownInner = styled.div `
  --light-bg-color: ${brand.primary.teal.tealBg};
  --bg-color: ${brand.primary.teal.teal25};
  --featured-cicle-color: ${brand.primary.teal.teal100};

  padding: 24px 73px 32px 24px;
  background-color: var(--bg-color);
  border-radius: 24px;

  margin-top: 8px;

  [data-has-featured='true'] & {
    display: grid;
    grid-template-columns: 519px 422px;
    gap: 104px;
    padding: 24px 48px 24px 32px;
  }

  &[data-has-child='true'] {
    display: grid;
    grid-template-columns: 430px 379px;
    gap: 0;
    padding: 0;
  }
`;
const ThirdLevelChild = styled.div ``;
const NavFeaturedLinks = styled.ul `
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: -56px;
    width: 1px;
    height: 100%;
    background-color: ${brand.global.white};
  }
`;
const NavNormalLinks = styled.ul `
  [data-has-child='true'] & {
    padding: 24px 0;
    background-color: var(--light-bg-color);
    border-radius: 24px 0 0 24px;
  }
`;
const NavDropdownItem = styled.li `
  margin-bottom: 13px;

  [data-has-child='true'] & {
    padding: 6px 32px;
    border-bottom: 1px solid ${brand.global.white};
    margin: 0;

    &[data-set-active='true'] {
      background-color: var(--bg-color);
    }
  }

  ${ThirdLevelChild} & {
    padding: 0;
    border: none;
    margin-bottom: 16px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;
const NavDropdownLink = styled.a `
  ${fonts.gotham.medium};
  font-size: 16px;
  line-height: 26px;
  color: ${brand.global.black};
  text-decoration: none;
  display: inline-block;

  &:hover,
  &:focus {
    ${fonts.gotham.regular};

    [data-has-child='true'] & {
      ${fonts.gotham.medium};
    }

    svg {
      transform: translateX(4px);
    }
  }

  &:focus {
    outline: 2px solid ${brand.global.black};
  }

  svg {
    ${transition('transform')};
    width: 20px;
    height: 20px;
  }
`;
const DropDownText = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const DropDownSummary = styled.div `
  ${fonts.gotham.regular};
  font-size: 14px;
  line-height: 24px;
  color: ${brand.global.black};
`;
const SrHasChild = styled.span `
  ${srOnly};
`;
const NavFeaturedDropdownItem = styled.li `
  position: relative;

  padding: 16px 66px 16px 0;
  border-bottom: 1px solid ${brand.global.white};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;
const NavLinkInner = styled.div ``;
const FeaturedLinkText = styled.span `
  ${fonts.gotham.ultra};
  margin-bottom: 8px;
`;
const FeaturedLinkSummary = styled.span `
  ${fonts.gotham.regular};
  font-size: 14px;
  line-height: 22px;
  color: ${brand.global.black};
`;
const CircleArrow = styled.span `
  width: 52px;
  height: 52px;
  border: 2px solid var(--featured-cicle-color);
  border-radius: 10000000000px;

  position: absolute;
  right: 0;
  bottom: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  &[data-border-color='pear'] {
    --featured-cicle-color: ${brand.primary.pear.pear100};
  }

  &[data-border-color='blue'] {
    --featured-cicle-color: ${brand.primary.blue.blue100};
  }

  &[data-border-color='dark-blue'] {
    --featured-cicle-color: ${brand.accent.darkBlue.darkBlue100};
  }

  &[data-border-color='forrest-green'] {
    --featured-cicle-color: ${brand.accent.forrestGreen.forrestGreen100};
  }

  &[data-border-color='purple'] {
    --featured-cicle-color: ${brand.secondary.purple.purple100};
  }

  &[data-border-color='yellow'] {
    --featured-cicle-color: ${brand.secondary.yellow.yellow100};
  }

  &[data-border-color='orange'] {
    --featured-cicle-color: ${brand.secondary.orange.orange100};
  }

  &[data-border-color='engage-green'] {
    --featured-cicle-color: ${brand.engagePrimary.green.green100};
  }

  &[data-border-color='engage-sea-green'] {
    --featured-cicle-color: ${brand.engagePrimary.seaGreen.seaGreen100};
  }

  &[data-border-color='engage-teal'] {
    --featured-cicle-color: ${brand.engagePrimary.teal.teal100};
  }

  &[data-border-color='engage-lime'] {
    --featured-cicle-color: ${brand.engagePrimary.lime.lime100};
  }

  &[data-border-color='engage-sky'] {
    --featured-cicle-color: ${brand.engagePrimary.sky.sky100};
  }

  &[data-border-color='engage-purple'] {
    --featured-cicle-color: ${brand.engageSecondary.purple.purple100};
  }

  &[data-border-color='engage-lilac'] {
    --featured-cicle-color: ${brand.engageSecondary.lilac.lilac100};
  }

  &[data-border-color='engage-tangerine'] {
    --featured-cicle-color: ${brand.engageSecondary.tangerine.tangerine100};
  }

  &[data-border-color='engage-lavender'] {
    --featured-cicle-color: ${brand.engageSecondary.lavender.lavender100};
  }

  &[data-border-color='engage-pink'] {
    --featured-cicle-color: ${brand.engageSecondary.pink.pink100};
  }

  &[data-border-color='engage-yellow'] {
    --featured-cicle-color: ${brand.engageSecondary.yellow.yellow100};
  }
`;
const NavChildDropdown = styled.ul `
  display: none;

  &[data-show-third-level='true'] {
    display: block;
  }

  ${ThirdLevelChild} & {
    padding: 32px;
  }
`;
const BurgerBtn = styled.button `
  ${transition('color')};
  ${paragraphRegular};
  ${fonts.gotham.medium};
  background: ${brand.accent.darkBlue.darkBlue100};
  border: none;
  padding: 0;
  font-size: ${fluid(8, 16)};
  line-height: ${fluid(14, 22)};
  width: 42px;
  height: 42px;
  border-radius: 100000000px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${brand.global.white};

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  [data-is-sticky='true'] & {
    background: ${brand.primary.teal.teal75};
  }

  div {
    display: grid;
    align-items: center;
  }

  svg {
    width: 24px;
  }

  [data-is-active='true'] & {
    svg {
      color: ${brand.global.white};
    }
  }
`;
const HeaderStyles = {
    BurgerBtn,
    CircleArrow,
    Container,
    CtaArea,
    DropDownSummary,
    SrHasChild,
    DropDownText,
    FeaturedLinkSummary,
    FeaturedLinkText,
    GlobalStyles,
    Inner,
    Logo,
    LogoLink,
    LogoSubText,
    LogoWrapper,
    MainNav,
    MobileSearchButton,
    Nav,
    NavChildDropdown,
    NavDropdown,
    NavDropDownInner,
    NavDropdownItem,
    NavDropdownLink,
    NavFeaturedDropdownItem,
    NavFeaturedLinks,
    NavItem,
    NavLink,
    NavLinkInner,
    NavNormalLinks,
    ScrolledSearchArea,
    SearchBox,
    SearchBoxContainer,
    SearchButton,
    SearchInput,
    SecondaryLogo,
    SecondaryLogoImg,
    ThirdLevelChild,
    TopArea,
    TopGrid,
};
export default HeaderStyles;
